import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Paper,
  Stack,
} from '@mui/material';
import {
  Close,
  CalendarToday,
  EventBusy,
} from '@mui/icons-material';
import { CalendarBooking } from '@/types';
import { BookingCard } from '@/components/Bookings/BookingCard';

interface DaySummaryPanelProps {
  selectedDate: Date;
  bookings: CalendarBooking[];
  open: boolean;
  onClose: () => void;
  onBookingClick?: (booking: CalendarBooking) => void;
}

const EmptyState: React.FC<{ date: Date }> = ({ date }) => {
  const formattedDate = date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        textAlign: 'center',
        backgroundColor: 'grey.50',
        borderRadius: 2,
        border: '1px dashed',
        borderColor: 'grey.300',
      }}
    >
      <EventBusy
        sx={{
          fontSize: 48,
          color: 'grey.400',
          mb: 2,
        }}
      />
      <Typography variant="h6" color="text.secondary" gutterBottom>
        No bookings for {formattedDate}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        This day is currently available for new bookings.
      </Typography>
    </Paper>
  );
};

const DayHeader: React.FC<{ date: Date; bookingCount: number; onClose: () => void }> = ({
  date,
  bookingCount,
  onClose,
}) => {
  const formattedDate = date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        position: 'sticky',
        top: 0,
        zIndex: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CalendarToday color="primary" />
        <Box>
          <Typography variant="h6" component="h2">
            {formattedDate}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {bookingCount === 0
              ? 'No bookings'
              : `${bookingCount} booking${bookingCount !== 1 ? 's' : ''}`}
          </Typography>
        </Box>
      </Box>
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
        aria-label="Close day summary"
      >
        <Close />
      </IconButton>
    </Box>
  );
};

export const DaySummaryPanel: React.FC<DaySummaryPanelProps> = ({
  selectedDate,
  bookings,
  open,
  onClose,
  onBookingClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleBookingClick = (booking: CalendarBooking) => {
    onBookingClick?.(booking);
  };

  // Convert CalendarBooking to Booking for BookingCard compatibility
  const handleBookingCardClick = (booking: any) => {
    // The booking passed here is already a CalendarBooking, so we can safely cast it
    handleBookingClick(booking as CalendarBooking);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      sx={{
        '& .MuiDrawer-paper': {
          width: isMobile ? '100%' : 400,
          maxWidth: '100vw',
          backgroundColor: 'background.default',
        },
      }}
      // Smooth slide animation
      transitionDuration={{
        enter: theme.transitions.duration.enteringScreen,
        exit: theme.transitions.duration.leavingScreen,
      }}
      // Add backdrop for mobile
      ModalProps={{
        keepMounted: false, // Better performance on mobile
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Day Header */}
        <DayHeader
          date={selectedDate}
          bookingCount={bookings.length}
          onClose={onClose}
        />

        {/* Content Area */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
          }}
        >
          {bookings.length === 0 ? (
            <EmptyState date={selectedDate} />
          ) : (
            <Stack spacing={2}>
              {bookings.map((booking) => (
                <BookingCard
                  key={booking.bookingId}
                  booking={booking}
                  listingTitle={booking.listingTitle}
                  onClick={handleBookingCardClick}
                  showGuestStatus={true}
                  showSource={true}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default DaySummaryPanel;